import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { NavBar } from '../components/NavBar';
import { 
  ImagePlus, Trash2, X, GitCompare, ArrowLeftRight, Loader2, Settings
} from 'lucide-react';
import { motion } from 'framer-motion';
import { toast } from 'sonner';

// Import refactored components
import { ProfileCard } from '../components/profile/ProfileCard';
import { ProfileEditForm } from '../components/profile/ProfileEditForm';
import { ActiveSessions } from '../components/profile/ActiveSessions';
import { ProgressComparisonSlider } from '../components/profile/ProgressComparisonSlider';
import { ProgressLightbox } from '../components/profile/ProgressLightbox';
import { bodyToUnit, unitToLb, unitLabel } from '../components/strength/units';
import { useWeightUnit } from '../hooks/useWeightUnit';

const API = process.env.REACT_APP_BACKEND_URL;

export const ProfilePage = () => {
  const { user, logout, updateProfile, token, api } = useAuth();
  const { unit: weightUnit } = useWeightUnit();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [goalDate, setGoalDate] = useState(user?.goal_deadline ? new Date(user.goal_deadline) : null);
  const [calendarOpen, setCalendarOpen] = useState(false);
  const [selectedGoals, setSelectedGoals] = useState(user?.fitness_goals || []);
  const [experienceLevel, setExperienceLevel] = useState(user?.experience_level || '');
  const [dietaryRestrictions, setDietaryRestrictions] = useState(user?.dietary_restrictions || []);
  const [customDietaryNotes, setCustomDietaryNotes] = useState(user?.custom_dietary_notes || '');
  const [weeklyGoal, setWeeklyGoal] = useState(user?.weekly_goal || 5);
  // Format E.164 phone (+14155551234) back into pretty (415) 555-1234 for the form
  const formatPhoneForDisplay = (e164) => {
    if (!e164) return '';
    const d = String(e164).replace(/\D/g, '');
    const ten = d.length === 11 && d.startsWith('1') ? d.slice(1) : d;
    if (ten.length !== 10) return e164;
    return `(${ten.slice(0,3)}) ${ten.slice(3,6)}-${ten.slice(6)}`;
  };

  const [formData, setFormData] = useState({
    name: user?.name || '',
    age: user?.age || '',
    weight: bodyToUnit(user?.weight, user?.weight_unit === 'kg' ? 'kg' : 'lb'),
    height: user?.height || '',
    fitness_goal: user?.fitness_goal || '',
    location: user?.location || '',
    bio: user?.bio || '',
    phone: formatPhoneForDisplay(user?.phone),
    sms_reminders_enabled: !!user?.sms_reminders_enabled,
    assignment_emails_enabled: user?.assignment_emails_enabled !== false,
  });
  
  // Re-sync formData when user data changes (e.g., after login or profile update)
  useEffect(() => {
    if (user) {
      setFormData({
        name: user.name || '',
        age: user.age || '',
        weight: bodyToUnit(user.weight, user.weight_unit === 'kg' ? 'kg' : 'lb'),
        height: user.height || '',
        fitness_goal: user.fitness_goal || '',
        location: user.location || '',
        bio: user.bio || '',
        phone: formatPhoneForDisplay(user.phone),
        sms_reminders_enabled: !!user.sms_reminders_enabled,
        assignment_emails_enabled: user.assignment_emails_enabled !== false,
      });
      setSelectedGoals(user.fitness_goals || []);
      setExperienceLevel(user.experience_level || '');
      setDietaryRestrictions(user.dietary_restrictions || []);
      setCustomDietaryNotes(user.custom_dietary_notes || '');
      setGoalDate(user.goal_deadline ? new Date(user.goal_deadline) : null);
      setProfilePicture(user.profile_picture || null);
    }
  }, [user]);
  
  // Profile picture state
  const [profilePicture, setProfilePicture] = useState(user?.profile_picture || null);
  const [uploadingProfile, setUploadingProfile] = useState(false);
  
  // Progress pictures state
  const [progressPictures, setProgressPictures] = useState([]);
  const [loadingProgress, setLoadingProgress] = useState(true);
  const [uploadingProgress, setUploadingProgress] = useState(false);
  const [progressCaption, setProgressCaption] = useState('');
  const [progressWeight, setProgressWeight] = useState('');
  const progressInputRef = useRef(null);
  
  // Lightbox state
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState(0);
  
  // Before/After comparison state
  const [compareMode, setCompareMode] = useState(false);
  const [beforeImage, setBeforeImage] = useState(null);
  const [afterImage, setAfterImage] = useState(null);
  const [sliderPosition, setSliderPosition] = useState(50);
  const [isDragging, setIsDragging] = useState(false);

  // Push Notification state
  const [notificationsEnabled, setNotificationsEnabled] = useState(false);
  const [notificationSettings, setNotificationSettings] = useState({
    enabled: true,
    workout_reminder: true,
    workout_time: '09:00',
    days: ['monday', 'tuesday', 'wednesday', 'thursday', 'friday'],
    message_notifications: true,
    progress_reminders: true
  });
  const [loadingNotifications, setLoadingNotifications] = useState(false);
  const [showNotificationSettings, setShowNotificationSettings] = useState(false);

  // Subscription state
  const [isPremium, setIsPremium] = useState(false);
  const [subscriptionPlan, setSubscriptionPlan] = useState(null);

  // Load progress pictures on mount
  useEffect(() => {
    const loadProgressPictures = async () => {
      try {
        const res = await api().get('/progress/pictures');
        setProgressPictures(res.data);
      } catch {
        // silent
      } finally {
        setLoadingProgress(false);
      }
    };
    loadProgressPictures();
  }, [api]);

  // Load subscription status
  useEffect(() => {
    const loadSubscriptionStatus = async () => {
      try {
        const res = await api().get('/subscriptions/status');
        setIsPremium(res.data.is_subscribed);
        if (res.data.subscription?.plan_id) {
          setSubscriptionPlan(res.data.subscription.plan_id);
        }
      } catch {
        // silent
      }
    };
    loadSubscriptionStatus();
  }, [api]);

  // Load notification status
  useEffect(() => {
    const loadNotificationStatus = async () => {
      try {
        const res = await api().get('/push/status');
        setNotificationsEnabled(res.data.subscribed);
        if (res.data.settings) {
          setNotificationSettings(res.data.settings);
        }
      } catch {
        // silent
      }
    };
    loadNotificationStatus();
  }, [api]);

  // Helper function for push notification key
  const urlBase64ToUint8Array = (base64String) => {
    const padding = '='.repeat((4 - base64String.length % 4) % 4);
    const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
    const rawData = window.atob(base64);
    const outputArray = new Uint8Array(rawData.length);
    for (let i = 0; i < rawData.length; ++i) {
      outputArray[i] = rawData.charCodeAt(i);
    }
    return outputArray;
  };

  // Notification handlers
  const enableNotifications = async () => {
    setLoadingNotifications(true);
    try {
      if (!('Notification' in window)) {
        toast.error('Notifications are not supported in this browser');
        return;
      }

      const permission = await Notification.requestPermission();
      if (permission !== 'granted') {
        toast.error('Notification permission denied');
        return;
      }

      const registration = await navigator.serviceWorker.register('/sw.js');
      await navigator.serviceWorker.ready;

      const subscription = await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array('BEl62iUYgUivxIkv69yViEuiBIa-Ib9-SkvMeAtA3LFgDzkrxZJjSgSnfckjBJuBkr3qBUYIHBQFLXYp5Nksh8U')
      });

      await api().post('/push/subscribe', subscription.toJSON());
      
      setNotificationsEnabled(true);
      toast.success('Notifications enabled! You\'ll receive workout reminders.');
    } catch (err) {
      toast.error('Failed to enable notifications');
    }
    setLoadingNotifications(false);
  };

  const disableNotifications = async () => {
    setLoadingNotifications(true);
    try {
      await api().delete('/push/unsubscribe');
      setNotificationsEnabled(false);
      toast.success('Notifications disabled');
    } catch (err) {
      toast.error('Failed to disable notifications');
    }
    setLoadingNotifications(false);
  };

  const saveNotificationSettings = async () => {
    try {
      await api().put('/push/settings', notificationSettings);
      toast.success('Notification settings saved');
      setShowNotificationSettings(false);
    } catch (err) {
      toast.error('Failed to save settings');
    }
  };

  const toggleDay = (day) => {
    setNotificationSettings(prev => ({
      ...prev,
      days: prev.days.includes(day)
        ? prev.days.filter(d => d !== day)
        : [...prev, day]
    }));
  };

  // Update profile picture when user changes
  useEffect(() => {
    if (user?.profile_picture) {
      setProfilePicture(user.profile_picture);
    }
  }, [user]);

  // Profile picture upload handler
  const handleProfilePictureUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    const allowedTypes = ['image/jpeg', 'image/png', 'image/webp', 'image/gif'];
    if (!allowedTypes.includes(file.type)) {
      toast.error('Invalid file type. Please upload a JPEG, PNG, WebP, or GIF image.');
      return;
    }
    
    if (file.size > 5 * 1024 * 1024) {
      toast.error('File too large. Maximum 5MB allowed.');
      return;
    }
    
    setUploadingProfile(true);
    const formDataUpload = new FormData();
    formDataUpload.append('file', file);
    
    try {
      const res = await fetch(`${API}/api/profile/picture`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        credentials: 'include',
        body: formDataUpload
      });
      
      if (!res.ok) {
        const error = await res.json();
        throw new Error(error.detail || 'Upload failed');
      }
      
      const data = await res.json();
      setProfilePicture(data.image_url);
      toast.success('Profile picture updated!');
    } catch (err) {
      toast.error(err.message || 'Failed to upload profile picture');
    } finally {
      setUploadingProfile(false);
    }
  };

  // Progress picture upload handler
  const handleProgressPictureUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    const allowedTypes = ['image/jpeg', 'image/png', 'image/webp', 'image/gif'];
    if (!allowedTypes.includes(file.type)) {
      toast.error('Invalid file type. Please upload a JPEG, PNG, WebP, or GIF image.');
      return;
    }
    
    if (file.size > 5 * 1024 * 1024) {
      toast.error('File too large. Maximum 5MB allowed.');
      return;
    }
    
    setUploadingProgress(true);
    const formDataUpload = new FormData();
    formDataUpload.append('file', file);
    if (progressCaption) formDataUpload.append('caption', progressCaption);
    if (progressWeight) formDataUpload.append('weight_at_time', String(unitToLb(parseFloat(progressWeight), weightUnit)));
    
    try {
      const res = await fetch(`${API}/api/progress/pictures`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        credentials: 'include',
        body: formDataUpload
      });
      
      if (!res.ok) {
        const error = await res.json();
        throw new Error(error.detail || 'Upload failed');
      }
      
      const data = await res.json();
      setProgressPictures(prev => [data.picture, ...prev]);
      setProgressCaption('');
      setProgressWeight('');
      toast.success('Progress picture uploaded!');
    } catch (err) {
      toast.error(err.message || 'Failed to upload progress picture');
    } finally {
      setUploadingProgress(false);
    }
  };

  const handleDeleteProgressPicture = async (pictureId) => {
    if (!window.confirm('Are you sure you want to delete this progress picture?')) return;
    
    try {
      await api().delete(`/progress/pictures/${pictureId}`);
      setProgressPictures(prev => prev.filter(p => p.id !== pictureId));
      toast.success('Progress picture deleted');
    } catch (err) {
      toast.error('Failed to delete picture');
    }
  };

  // Lightbox handlers
  const openLightbox = (index) => {
    setLightboxIndex(index);
    setLightboxOpen(true);
  };

  const nextImage = () => {
    setLightboxIndex((prev) => (prev + 1) % progressPictures.length);
  };

  const prevImage = () => {
    setLightboxIndex((prev) => (prev - 1 + progressPictures.length) % progressPictures.length);
  };

  // Before/After comparison handlers
  const selectForComparison = (pic, type) => {
    if (type === 'before') {
      setBeforeImage(pic);
      toast.success('Before image selected');
    } else {
      setAfterImage(pic);
      toast.success('After image selected');
    }
  };

  const clearComparison = () => {
    setBeforeImage(null);
    setAfterImage(null);
    setCompareMode(false);
    setSliderPosition(50);
  };

  // Form handlers
  const toggleGoal = (goalId) => {
    setSelectedGoals(prev => 
      prev.includes(goalId) 
        ? prev.filter(g => g !== goalId)
        : [...prev, goalId]
    );
  };

  const handleDateSelect = (date) => {
    setGoalDate(date);
    setCalendarOpen(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await updateProfile({
        name: formData.name,
        age: formData.age ? parseInt(formData.age) : null,
        weight: formData.weight ? unitToLb(parseFloat(formData.weight), weightUnit) : null,
        height: formData.height ? parseFloat(formData.height) : null,
        fitness_goal: formData.fitness_goal || null,
        goal_deadline: goalDate ? goalDate.toISOString() : null,
        fitness_goals: selectedGoals,
        experience_level: experienceLevel || null,
        dietary_restrictions: dietaryRestrictions,
        custom_dietary_notes: customDietaryNotes || null,
        location: formData.location || null,
        bio: formData.bio || null,
        // Phone is normalized to E.164 (+1XXXXXXXXXX) so SMS reset can find
        // this user. Backend also normalizes, so we're being defensive.
        phone: (() => {
          const d = (formData.phone || '').replace(/\D/g, '');
          if (!d) return null;
          if (d.length === 10) return `+1${d}`;
          if (d.length === 11 && d.startsWith('1')) return `+${d}`;
          return null;
        })(),
        sms_reminders_enabled: !!formData.sms_reminders_enabled,
        assignment_emails_enabled: formData.assignment_emails_enabled !== false,
        weekly_goal: weeklyGoal
      });
      toast.success('Profile updated successfully!');
    } catch (err) {
      toast.error('Failed to update profile');
    }
    setLoading(false);
  };

  const handleLogout = () => {
    logout();
    navigate('/');
    toast.success('Logged out successfully');
  };

  // Calculate days until goal
  const daysUntilGoal = goalDate ? Math.ceil((goalDate - new Date()) / (1000 * 60 * 60 * 24)) : null;

  return (
    <div className="min-h-screen bg-black" data-testid="profile-page">
      <NavBar title="Profile" />

      <main className="container mx-auto px-4 sm:px-6 py-8 max-w-7xl">
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-8 sm:mb-10"
        >
          <div className="inline-flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-[0.2em] text-primary bg-primary/[0.08] border border-primary/25 rounded-full px-2.5 py-1 mb-3">
            <Settings className="h-3 w-3" /> Account
          </div>
          <h2 className="font-heading text-3xl sm:text-4xl md:text-5xl uppercase tracking-wider leading-[1.05] bg-gradient-to-b from-white to-white/70 bg-clip-text text-transparent">
            Your <span className="text-primary">Profile</span>
          </h2>
          <p className="text-muted-foreground mt-2 text-sm sm:text-base">Manage your account and fitness goals</p>
        </motion.div>

        <div className="grid lg:grid-cols-3 gap-8">
          {/* Profile Card Component */}
          <ProfileCard
            user={user}
            profilePicture={profilePicture}
            uploadingProfile={uploadingProfile}
            onProfilePictureUpload={handleProfilePictureUpload}
            formData={formData}
            goalDate={goalDate}
            daysUntilGoal={daysUntilGoal}
            notificationsEnabled={notificationsEnabled}
            loadingNotifications={loadingNotifications}
            onEnableNotifications={enableNotifications}
            onDisableNotifications={disableNotifications}
            showNotificationSettings={showNotificationSettings}
            onToggleNotificationSettings={() => setShowNotificationSettings(!showNotificationSettings)}
            notificationSettings={notificationSettings}
            onNotificationSettingsChange={setNotificationSettings}
            onSaveNotificationSettings={saveNotificationSettings}
            onToggleDay={toggleDay}
            isPremium={isPremium}
            subscriptionPlan={subscriptionPlan}
          />

          {/* Profile Edit Form Component */}
          <ProfileEditForm
            user={user}
            formData={formData}
            onFormChange={setFormData}
            selectedGoals={selectedGoals}
            onToggleGoal={toggleGoal}
            experienceLevel={experienceLevel}
            onExperienceLevelChange={setExperienceLevel}
            dietaryRestrictions={dietaryRestrictions}
            onToggleDietaryRestriction={(id) => setDietaryRestrictions(prev => prev.includes(id) ? prev.filter(d => d !== id) : [...prev, id])}
            customDietaryNotes={customDietaryNotes}
            onCustomDietaryNotesChange={setCustomDietaryNotes}
            weeklyGoal={weeklyGoal}
            onWeeklyGoalChange={setWeeklyGoal}
            goalDate={goalDate}
            onDateSelect={handleDateSelect}
            calendarOpen={calendarOpen}
            onCalendarOpenChange={setCalendarOpen}
            daysUntilGoal={daysUntilGoal}
            loading={loading}
            onSubmit={handleSubmit}
            onLogout={handleLogout}
          />

          {/* Active Sessions (security) */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="relative rounded-2xl border border-white/10 bg-gradient-to-b from-zinc-900/80 to-black/60 shadow-[0_8px_30px_rgba(0,0,0,0.35)] p-6 overflow-hidden"
          >
            <div className="pointer-events-none absolute -top-16 -right-16 h-40 w-40 rounded-full bg-primary/5 blur-3xl" />
            <div className="relative">
              <ActiveSessions api={api} />
            </div>
          </motion.div>
        </div>

        {/* Progress Pictures Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="mt-10"
        >
          <div className="relative rounded-2xl border border-white/10 bg-gradient-to-b from-zinc-900/80 to-black/60 shadow-[0_8px_30px_rgba(0,0,0,0.35)] p-6 sm:p-8 overflow-hidden">
            <div className="pointer-events-none absolute -top-24 -right-24 h-56 w-56 rounded-full bg-primary/8 blur-3xl" />
            <div className="pointer-events-none absolute -bottom-24 -left-24 h-48 w-48 rounded-full bg-primary/5 blur-3xl" />

            <div className="relative">
            <div className="flex items-start justify-between mb-6 flex-wrap gap-4">
              <div>
                <div className="inline-flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-[0.2em] text-primary bg-primary/[0.08] border border-primary/25 rounded-full px-2.5 py-1 mb-2">
                  <ImagePlus className="h-3 w-3" /> Photo Journal
                </div>
                <h3 className="font-heading text-2xl sm:text-3xl uppercase tracking-wider leading-tight bg-gradient-to-b from-white to-white/70 bg-clip-text text-transparent">
                  Progress <span className="text-primary">Gallery</span>
                </h3>
                <p className="text-muted-foreground text-sm mt-1">Track your fitness journey with photos</p>
              </div>
              <div className="flex items-center gap-2 flex-wrap">
                {progressPictures.length >= 2 && (
                  <button
                    onClick={() => setCompareMode(!compareMode)}
                    className={`inline-flex items-center gap-1.5 text-xs font-semibold rounded-full px-3.5 py-2 transition-all ${
                      compareMode
                        ? 'bg-primary text-black shadow-[0_4px_14px_rgba(250,204,21,0.25)]'
                        : 'text-primary bg-primary/[0.06] hover:bg-primary/[0.12] border border-primary/25 hover:border-primary/50'
                    }`}
                    data-testid="compare-toggle-btn"
                  >
                    <GitCompare className="h-3.5 w-3.5" />
                    {compareMode ? 'Exit Compare' : 'Compare'}
                  </button>
                )}
                <button
                  onClick={() => progressInputRef.current?.click()}
                  className="inline-flex items-center gap-1.5 text-xs font-semibold text-black bg-primary hover:bg-primary/90 rounded-full px-3.5 py-2 shadow-[0_4px_14px_rgba(250,204,21,0.25)] transition-all disabled:opacity-60"
                  disabled={uploadingProgress}
                  data-testid="upload-progress-btn"
                >
                  {uploadingProgress ? (
                    <Loader2 className="h-3.5 w-3.5 animate-spin" />
                  ) : (
                    <ImagePlus className="h-3.5 w-3.5" />
                  )}
                  Add Photo
                </button>
              </div>
              <input
                ref={progressInputRef}
                type="file"
                accept="image/jpeg,image/png,image/webp,image/gif"
                onChange={handleProgressPictureUpload}
                className="hidden"
                data-testid="progress-picture-input"
              />
            </div>

            {/* Before/After Comparison Mode Instructions */}
            {compareMode && (
              <motion.div
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                className="mb-6 relative rounded-2xl border border-primary/25 bg-gradient-to-br from-primary/[0.08] via-primary/[0.03] to-transparent p-5 overflow-hidden"
              >
                <div className="pointer-events-none absolute -top-12 -right-12 h-32 w-32 rounded-full bg-primary/10 blur-3xl" />
                <div className="relative">
                  <div className="flex items-center gap-2 mb-2">
                    <span className="h-8 w-8 rounded-full bg-primary/15 border border-primary/30 flex items-center justify-center flex-shrink-0">
                      <ArrowLeftRight className="h-4 w-4 text-primary" />
                    </span>
                    <span className="font-heading text-xs uppercase tracking-[0.2em] text-primary">Before & After Comparison</span>
                  </div>
                  <p className="text-sm text-muted-foreground mb-3">
                    Click on photos below to select them as "Before" or "After" for side-by-side comparison.
                  </p>
                  <div className="flex items-center gap-3 flex-wrap">
                    <span className={`inline-flex items-center gap-1.5 text-[11px] font-medium rounded-full px-2.5 py-1 border ${beforeImage ? 'text-blue-300 bg-blue-500/[0.08] border-blue-500/30' : 'text-muted-foreground bg-white/[0.03] border-white/10'}`}>
                      <span className={`h-1.5 w-1.5 rounded-full ${beforeImage ? 'bg-blue-400 shadow-[0_0_6px_currentColor]' : 'bg-white/20'}`} />
                      Before: {beforeImage ? 'Selected' : 'Not selected'}
                    </span>
                    <span className={`inline-flex items-center gap-1.5 text-[11px] font-medium rounded-full px-2.5 py-1 border ${afterImage ? 'text-emerald-300 bg-emerald-500/[0.08] border-emerald-500/30' : 'text-muted-foreground bg-white/[0.03] border-white/10'}`}>
                      <span className={`h-1.5 w-1.5 rounded-full ${afterImage ? 'bg-emerald-400 shadow-[0_0_6px_currentColor]' : 'bg-white/20'}`} />
                      After: {afterImage ? 'Selected' : 'Not selected'}
                    </span>
                    {(beforeImage || afterImage) && (
                      <button
                        onClick={clearComparison}
                        className="ml-auto inline-flex items-center gap-1.5 text-xs font-medium text-red-300 bg-red-500/[0.06] hover:bg-red-500/[0.12] border border-red-500/25 hover:border-red-500/50 rounded-full px-3 py-1.5 transition-all"
                      >
                        <X className="h-3.5 w-3.5" />
                        Clear Selection
                      </button>
                    )}
                  </div>
                </div>
              </motion.div>
            )}

            {/* Before/After Comparison View */}
            {compareMode && beforeImage && afterImage && (
              <ProgressComparisonSlider
                beforeImage={beforeImage}
                afterImage={afterImage}
                sliderPosition={sliderPosition}
                isDragging={isDragging}
                onDragStart={() => setIsDragging(true)}
                onDragEnd={() => setIsDragging(false)}
                onDragMove={setSliderPosition}
                onTouchMove={setSliderPosition}
              />
            )}

            {/* Upload form fields */}
            <div className="grid md:grid-cols-2 gap-4 mb-6 p-4 rounded-2xl bg-white/[0.02] border border-white/10">
              <div className="space-y-2">
                <Label className="text-white uppercase tracking-wide text-sm">Caption (optional)</Label>
                <Input
                  value={progressCaption}
                  onChange={(e) => setProgressCaption(e.target.value)}
                  placeholder="e.g., Week 4 progress"
                  className="h-10 bg-black/60 border-white/10 focus:border-primary text-white rounded-lg"
                  data-testid="progress-caption-input"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-white uppercase tracking-wide text-sm">Weight at Time ({unitLabel(weightUnit)}, optional)</Label>
                <Input
                  type="number"
                  step="0.1"
                  value={progressWeight}
                  onChange={(e) => setProgressWeight(e.target.value)}
                  placeholder={weightUnit === 'kg' ? 'e.g., 79' : 'e.g., 175'}
                  className="h-10 bg-black/60 border-white/10 focus:border-primary text-white rounded-lg"
                  data-testid="progress-weight-input"
                />
              </div>
            </div>

            {/* Progress Pictures Grid */}
            {loadingProgress ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="h-8 w-8 text-primary animate-spin" />
              </div>
            ) : progressPictures.length === 0 ? (
              <div className="text-center py-12 rounded-2xl border border-dashed border-white/15 bg-white/[0.02]">
                <div className="h-14 w-14 rounded-full bg-primary/10 border border-primary/25 flex items-center justify-center mx-auto mb-4">
                  <ImagePlus className="h-6 w-6 text-primary" />
                </div>
                <p className="text-white font-medium mb-1">No progress pictures yet</p>
                <p className="text-sm text-muted-foreground">Upload your first photo to start tracking your journey!</p>
              </div>
            ) : (
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3" data-testid="progress-gallery">
                {progressPictures.map((pic, index) => {
                  const isSelectedBefore = beforeImage?.id === pic.id;
                  const isSelectedAfter = afterImage?.id === pic.id;
                  
                  return (
                    <motion.div
                      key={pic.id}
                      initial={{ opacity: 0, scale: 0.9 }}
                      animate={{ opacity: 1, scale: 1 }}
                      className={`relative group aspect-square bg-black/50 rounded-2xl overflow-hidden cursor-pointer border transition-all ${
                        isSelectedBefore ? 'border-blue-500 ring-2 ring-blue-500/30' :
                        isSelectedAfter ? 'border-emerald-500 ring-2 ring-emerald-500/30' :
                        'border-white/10 hover:border-primary/40'
                      }`}
                      onClick={() => compareMode ? null : openLightbox(index)}
                      data-testid={`progress-picture-${index}`}
                    >
                      <img
                        src={`${API}${pic.image_url}`}
                        alt={pic.caption || 'Progress photo'}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                      />
                      
                      {/* Selection indicators for compare mode */}
                      {isSelectedBefore && (
                        <div className="absolute top-2 left-2 px-2 py-1 bg-blue-500 text-white text-[10px] font-heading uppercase tracking-wider rounded-full">
                          Before
                        </div>
                      )}
                      {isSelectedAfter && (
                        <div className="absolute top-2 left-2 px-2 py-1 bg-emerald-500 text-white text-[10px] font-heading uppercase tracking-wider rounded-full">
                          After
                        </div>
                      )}
                      
                      {/* Compare mode selection buttons */}
                      {compareMode && !isSelectedBefore && !isSelectedAfter && (
                        <div className="absolute inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              selectForComparison(pic, 'before');
                            }}
                            className="px-3 py-1.5 bg-blue-500 hover:bg-blue-400 text-white text-[10px] font-heading uppercase tracking-wider rounded-full transition-colors"
                            data-testid={`select-before-${index}`}
                          >
                            Set Before
                          </button>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              selectForComparison(pic, 'after');
                            }}
                            className="px-3 py-1.5 bg-emerald-500 hover:bg-emerald-400 text-white text-[10px] font-heading uppercase tracking-wider rounded-full transition-colors"
                            data-testid={`select-after-${index}`}
                          >
                            Set After
                          </button>
                        </div>
                      )}
                      
                      {/* Normal mode overlay */}
                      {!compareMode && (
                        <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity">
                          <div className="absolute bottom-0 left-0 right-0 p-3">
                            {pic.caption && (
                              <p className="text-white text-sm font-medium truncate">{pic.caption}</p>
                            )}
                            <div className="flex items-center justify-between mt-1">
                              <p className="text-[11px] text-white/60">
                                {new Date(pic.created_at).toLocaleDateString()}
                              </p>
                              {pic.weight_at_time && (
                                <p className="text-[11px] text-primary font-heading font-semibold">{bodyToUnit(pic.weight_at_time, weightUnit)} {unitLabel(weightUnit)}</p>
                              )}
                            </div>
                          </div>
                        </div>
                      )}
                      
                      {/* Delete button (only in non-compare mode) */}
                      {!compareMode && (
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDeleteProgressPicture(pic.id);
                          }}
                          className="absolute top-2 right-2 h-8 w-8 bg-red-500/90 backdrop-blur-sm rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity hover:bg-red-500 shadow-lg"
                          data-testid={`delete-progress-${index}`}
                        >
                          <Trash2 className="h-3.5 w-3.5 text-white" />
                        </button>
                      )}
                    </motion.div>
                  );
                })}
              </div>
            )}
            </div>
          </div>
        </motion.div>
      </main>

      {/* Lightbox Modal */}
      <ProgressLightbox
        isOpen={lightboxOpen}
        pictures={progressPictures}
        currentIndex={lightboxIndex}
        onClose={() => setLightboxOpen(false)}
        onNext={nextImage}
        onPrev={prevImage}
      />
    </div>
  );
};
